/**
 * ForgotPassword — public page to request a password reset link.
 *
 * URL: /forgot-password
 *
 * Always shows the same "if registered, we sent a reset link" success
 * message regardless of whether the email exists — backend enforces this
 * too (anti-enumeration).
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authApi } from '@/api/client';
import {
  Button,
  Input,
  Card,
  CardContent,
  GradientText,
  ParticleBackground,
} from '@/components/common';
import { Mail, ArrowLeft, Key, CheckCircle2 } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
    } catch {
      // We don't reveal failures here — anti-enumeration means
      // we show the same success state regardless.
    } finally {
      setSubmitted(true);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <ParticleBackground particleCount={60} />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="absolute top-6 left-6 z-20"
      >
        <Link to="/login" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors">
          <ArrowLeft size={20} />
          <span>Back to Sign In</span>
        </Link>
      </motion.div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="p-3 rounded-xl bg-primary-50 border border-primary/20">
                <Key className="w-10 h-10 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold mb-2">
              <GradientText text="Reset Password" />
            </h1>
            <p className="text-gray-500">Enter your email and we'll send a reset link</p>
          </div>

          <Card variant="glass" padding="lg">
            <CardContent>
              {submitted ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="mx-auto w-12 h-12 text-emerald-500 mb-4" />
                  <p className="text-gray-900 font-medium mb-2">Check your inbox</p>
                  <p className="text-gray-500 text-sm">
                    If <strong>{email}</strong> is registered, we've sent a
                    password reset link. The link expires in 1 hour.
                  </p>
                  <p className="text-xs text-gray-400 mt-6">
                    Didn't get an email? Wait a few minutes, check your spam
                    folder, then try again. Repeated attempts are rate-limited.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    leftIcon={<Mail size={18} />}
                    fullWidth
                    required
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    isLoading={isSubmitting}
                    disabled={!email}
                  >
                    Send Reset Link
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-center text-xs text-gray-600 mt-8"
          >
            &copy; 2014-2026 MadHat Media, Inc. All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
